import { EXPORT_KINDS, REPORT_KINDS, type Report, type ReportKind } from '@beta-assur/shared';
import { useState } from 'react';
import { Field, PageHeader } from '../components/page';
import { ActionError, Empty, QueryView } from '../components/states';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input, Select } from '../components/ui/input';
import { Table, TBody, TD, TH, THead, TR } from '../components/ui/table';
import { useAuth } from '../lib/auth';
import { day, instant, money } from '../lib/format';
import { useI18n } from '../lib/i18n';
import { useApiQuery } from '../lib/query';
import { useReferentials, useTenant } from '../lib/referentials';
import { Tabs } from './portfolio';
import { download } from './policyholder-rights';

// Rapports (BA-055) et exports (BA-056, spec 12) : sept rapports sur une période, exportables
// en CSV ou XLSX, exports des listes et rapports mensuels déposés chaque 1er du mois.

type Cell = Report['rows'][number][string];
type CellType = Report['columns'][number]['type'];

function useFormat() {
  const { language } = useI18n();
  return (value: Cell | undefined, type: CellType) => {
    if (value === null || value === undefined) return '-';
    if (typeof value === 'string') return value;
    if (type === 'money') return money(value, language);
    if (type === 'percent') return `${(Math.round(value * 1000) / 10).toLocaleString(language)} %`;
    return value.toLocaleString(language);
  };
}

/** Premier jour du mois et aujourd'hui, au format ISO, dans le fuseau du navigateur. */
function currentMonth(): { from: string; to: string } {
  const today = new Date().toISOString().slice(0, 10);
  return { from: `${today.slice(0, 8)}01`, to: today };
}

function ReportView({ report }: { report: Report }) {
  const t = useI18n().t;
  const format = useFormat();
  return (
    <div className="flex flex-col gap-4">
      {report.summary.length ? (
        <ul
          className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4"
          aria-label={t.reports.summary}
        >
          {report.summary.map((s) => (
            <li key={s.label} className="rounded-xl border border-border bg-card p-4 shadow-card">
              <p className="text-sm text-muted-foreground">{s.label}</p>
              <p className="mt-1 text-2xl font-semibold tabular-nums">{format(s.value, s.type)}</p>
              {s.previous !== undefined ? (
                <p className="mt-1 text-xs text-muted-foreground">
                  {t.reports.previous} : {format(s.previous, s.type)}
                </p>
              ) : null}
            </li>
          ))}
        </ul>
      ) : null}
      <Card>
        {report.rows.length === 0 ? (
          <Empty />
        ) : (
          <Table>
            <THead>
              <tr>
                {report.columns.map((c) => (
                  <TH key={c.key} className={c.type === 'text' ? undefined : 'text-end'}>
                    {c.label}
                  </TH>
                ))}
              </tr>
            </THead>
            <TBody>
              {report.rows.map((row, i) => (
                <TR key={i}>
                  {report.columns.map((c) => (
                    <TD
                      key={c.key}
                      className={c.type === 'text' ? undefined : 'text-end tabular-nums'}
                    >
                      {format(row[c.key], c.type)}
                    </TD>
                  ))}
                </TR>
              ))}
            </TBody>
          </Table>
        )}
      </Card>
    </div>
  );
}

function ExportsCard() {
  const { t, language } = useI18n();
  const { api } = useAuth();
  const [error, setError] = useState<Error | null>(null);
  const files = useApiQuery<{
    items: {
      id: string;
      kind: string;
      period: string | null;
      filename: string;
      createdAt: string;
    }[];
  }>('/v1/exports-files');
  const tz = useTenant().data?.timezone;
  const get = (path: string, filename: string) => {
    setError(null);
    download(api, path, filename).catch((e: Error) => setError(e));
  };
  return (
    <div className="grid gap-4 lg:grid-cols-2">
      <Card>
        <CardHeader>
          <CardTitle>{t.reports.exports}</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-col gap-3">
          {EXPORT_KINDS.map((kind) => (
            <div key={kind} className="flex flex-wrap items-center justify-between gap-2">
              <span className="text-sm">{t.reports.exportKinds[kind]}</span>
              <span className="flex gap-2">
                {(['csv', 'xlsx'] as const).map((format) => (
                  <Button
                    key={format}
                    size="sm"
                    variant="outline"
                    onClick={() =>
                      get(
                        `/v1/exports/${kind}?format=${format}&lang=${language}`,
                        `export-${kind}.${format}`,
                      )
                    }
                  >
                    {format.toUpperCase()}
                  </Button>
                ))}
              </span>
            </div>
          ))}
          <ActionError error={error} />
        </CardContent>
      </Card>
      <Card>
        <CardHeader>
          <CardTitle>{t.reports.monthly}</CardTitle>
        </CardHeader>
        <QueryView query={files}>
          {(data) =>
            data.items.length === 0 ? (
              <Empty>{t.reports.noMonthly}</Empty>
            ) : (
              <Table>
                <TBody>
                  {data.items.map((f) => (
                    <TR key={f.id}>
                      <TD>{f.filename}</TD>
                      <TD className="text-muted-foreground">
                        {instant(f.createdAt, tz, language)}
                      </TD>
                      <TD className="text-end">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => get(`/v1/exports-files/${f.id}`, f.filename)}
                        >
                          {t.reports.download}
                        </Button>
                      </TD>
                    </TR>
                  ))}
                </TBody>
              </Table>
            )
          }
        </QueryView>
      </Card>
    </div>
  );
}

export function ReportsPage() {
  const { t, language } = useI18n();
  const { api, can } = useAuth();
  const insurers = useReferentials().insurers;
  const [kind, setKind] = useState<ReportKind>('recovery');
  const [period, setPeriod] = useState(currentMonth);
  const [insurerId, setInsurerId] = useState('');
  const [error, setError] = useState<Error | null>(null);
  // Libellés du rapport et des exports dans la langue du portail.
  const query = {
    from: period.from,
    to: period.to,
    lang: language,
    ...(insurerId ? { insurerId } : {}),
  };
  const report = useApiQuery<Report>(`/v1/reports/${kind}`, query);
  const exportReport = (format: 'csv' | 'xlsx') => {
    setError(null);
    const search = new URLSearchParams({ ...query, format }).toString();
    download(
      api,
      `/v1/reports/${kind}?${search}`,
      `rapport-${kind}-${period.from}-${period.to}.${format}`,
    ).catch((e: Error) => setError(e));
  };

  return (
    <>
      <PageHeader title={t.reports.title} />
      <Tabs
        label={t.reports.title}
        active={kind}
        tabs={REPORT_KINDS.map((k) => ({ key: k, label: t.reports.kinds[k] }))}
        onChange={(k) => setKind(k as ReportKind)}
      />
      <Card className="mb-4">
        <CardContent className="flex flex-wrap items-end gap-3">
          <Field label={t.common.from}>
            <Input
              type="date"
              value={period.from}
              onChange={(e) => setPeriod((p) => ({ ...p, from: e.target.value }))}
            />
          </Field>
          <Field label={t.common.to}>
            <Input
              type="date"
              value={period.to}
              onChange={(e) => setPeriod((p) => ({ ...p, to: e.target.value }))}
            />
          </Field>
          <Field label={t.reports.insurer}>
            <Select value={insurerId} onChange={(e) => setInsurerId(e.target.value)}>
              <option value="">{t.common.all}</option>
              {insurers.map((i) => (
                <option key={i.id} value={i.id}>
                  {i.displayName}
                </option>
              ))}
            </Select>
          </Field>
          <span className="flex gap-2 sm:ms-auto">
            <Button variant="outline" onClick={() => exportReport('csv')}>
              {t.reports.exportCsv}
            </Button>
            <Button variant="outline" onClick={() => exportReport('xlsx')}>
              {t.reports.exportXlsx}
            </Button>
          </span>
        </CardContent>
        <p className="px-4 pb-3 text-xs text-muted-foreground">
          {t.reports.periodLabel} {day(period.from, language)} - {day(period.to, language)}
        </p>
        <div className="px-4 pb-3">
          <ActionError error={error} />
        </div>
      </Card>
      <QueryView query={report}>{(data) => <ReportView report={data} />}</QueryView>
      {can('editPortfolio') ? (
        <section className="mt-6" aria-label={t.reports.exports}>
          <ExportsCard />
        </section>
      ) : null}
    </>
  );
}
